import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Container,
  Row,
  Col,
  Form,
  Spinner,
  Alert,
  Button,
} from 'react-bootstrap';
import TitleWithBack from '../../components/common/TitleWithBack';

function WorkUploadedReport() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [examCentres, setExamCentres] = useState([]);
  const [modules, setModules] = useState([]);
  const [examSeries, setExamSeries] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);
  const [filters, setFilters] = useState({
    examCentre: '',
    module: '',
    examSeries: '',
  });

  // Fetch exam centre, module, and exam series data
  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoadingDropdowns(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [centresRes, seriesRes, modulesRes] = await Promise.all([
          axios.get('/api/exam-centres', { headers }),
          axios.get('/api/exam-series', { headers }),
          axios.get('/api/modules', { headers }),
        ]);
        setExamCentres(centresRes.data);
        setExamSeries(seriesRes.data);
        setModules(modulesRes.data);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
        setError('Failed to load dropdown data.');
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, []);

  const fetchReports = () => {
    if (!filters.examSeries) {
      setError('Please select an Exam Series.');
      return;
    }

    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');

    axios
      .get('/api/archiver/reports/work-uploaded', {
        params: filters,
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setReports(res.data);
      })
      .catch((err) => {
        console.error(err);
        setError('Error fetching report data.');
      })
      .finally(() => setLoading(false));
  };

  // Auto-fetch report when filters.examSeries changes
  useEffect(() => {
    if (filters.examSeries) fetchReports();
  }, [filters.examSeries]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Exam Summary Report', 14, 15);

    doc.setFontSize(10);
    doc.text(
      `Filters - Exam Centre: ${filters.examCentre || 'All'}, Module: ${filters.module || 'All'}, Exam Series: ${filters.examSeries || 'All'}`,
      14,
      23
    );

    const columns = ['#', 'Exam Centre', 'Centre Code', 'Module Code', 'Module Name', 'Student Count'];
    const rows = reports.map((r, i) => [
      i + 1,
      r.examCentreName || '',
      r.examCentreCode || '',
      r.moduleCode || '',
      r.moduleName || '',
      r.studentCount || 0,
    ]);

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 28,
    });

    doc.save('exam_summary_report.pdf');
  };

  if (loadingDropdowns) {
    return (
      <Container className="mt-2 text-center">
        <TitleWithBack title="Exam Summary Report" level="h2" />
        <Spinner animation="border" variant="primary" />
        <p>Loading dropdown data...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-2">
      <TitleWithBack title="Exam Summary Report" level="h2" />

      {/* Filter Section */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="examCentre">
            <Form.Label>Exam Centre (Optional)</Form.Label>
            <Form.Select
              name="examCentre"
              value={filters.examCentre}
              onChange={handleFilterChange}
            >
              <option value="">All Exam Centres</option>
              {examCentres.map((centre) => (
                <option key={centre.id} value={centre.centreName}>
                  {centre.centreName} ({centre.centreNumber})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="module">
            <Form.Label>Module (Optional)</Form.Label>
            <Form.Select
              name="module"
              value={filters.module}
              onChange={handleFilterChange}
            >
              <option value="">All Modules</option>
              {modules.map((module) => (
                <option key={module.id} value={module.moduleName}>
                  {module.moduleName} ({module.moduleCode})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="examSeries">
            <Form.Label>Exam Series *</Form.Label>
            <Form.Select
              name="examSeries"
              value={filters.examSeries}
              onChange={handleFilterChange}
              required
            >
              <option value="">Select Exam Series</option>
              {examSeries.map((series) => (
                <option key={series.id} value={series.name}>
                  {series.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Export PDF button */}
      <Row className="mb-3">
        <Col>
          <Button
            variant="primary"
            onClick={exportToPDF}
            disabled={reports.length === 0}
          >
            Generate PDF
          </Button>
        </Col>
      </Row>

      {/* Loading, Error, or Info Messages */}
      {loading && (
        <div className="text-center my-3">
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {!loading && !error && reports.length === 0 && filters.examSeries && (
        <Alert variant="info">Nothing to show here</Alert>
      )}
      {!loading && !error && !filters.examSeries && (
        <Alert variant="warning">Please select an Exam Series to view the report.</Alert>
      )}
    </Container>
  );
}

export default WorkUploadedReport;
